/**
 * Profile Completion Component
 * Modal shown to new users after signup to complete their profile
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, PawPrint, Cat, Dog, Bird, Home, Check, X } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";

import { API } from "../lib/api";

const ProfileCompletion = ({ isOpen, onClose, onComplete }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    location: "",
    bio: "",
    pet_type: "cats", // cats, dogs, other, none
    num_cats: 0,
    num_dogs: 0,
    num_other_pets: 0,
    other_pets_description: "",
    interested_in_house_sitting: false
  });

  const totalSteps = 3;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/profile`, formData, {
        withCredentials: true
      });
      toast.success("Profile completed! Welcome to CatPals+");
      onComplete?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save profile");
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onClose();
    toast.info("You can complete your profile later from your dashboard");
  };

  const nextStep = () => {
    if (step < totalSteps) setStep(step + 1);
    else handleSubmit();
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const renderStep1 = () => (
    <div className="space-y-6" data-testid="profile-step-1">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <MapPin className="w-8 h-8 text-amber-600" />
        </div>
        <h3 className="text-lg font-semibold text-stone-900">Where are you based?</h3>
        <p className="text-sm text-stone-500">Help us connect you with local services</p>
      </div>
      
      <div className="space-y-4">
        <div>
          <Label htmlFor="location">City / Area</Label>
          <Input
            id="location"
            placeholder="e.g., Norwich, Norfolk"
            value={formData.location}
            onChange={(e) => handleInputChange("location", e.target.value)}
            data-testid="profile-location-input"
          />
        </div>
        
        <div>
          <Label htmlFor="bio">Tell us about yourself (optional)</Label>
          <Textarea
            id="bio"
            placeholder="I'm a cat lover who..."
            value={formData.bio}
            onChange={(e) => handleInputChange("bio", e.target.value)}
            rows={3}
            data-testid="profile-bio-input"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6" data-testid="profile-step-2">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <PawPrint className="w-8 h-8 text-amber-600" />
        </div>
        <h3 className="text-lg font-semibold text-stone-900">Do you have any pets?</h3>
        <p className="text-sm text-stone-500">This helps us tailor our services for you</p>
      </div>

      <RadioGroup 
        value={formData.pet_type} 
        onValueChange={(v) => handleInputChange("pet_type", v)}
        className="grid grid-cols-2 gap-3"
        data-testid="profile-pet-type-group"
      >
        <Label 
          htmlFor="pet-cats" 
          className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all ${formData.pet_type === 'cats' ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:border-stone-300'}`}
        >
          <RadioGroupItem value="cats" id="pet-cats" />
          <Cat className="w-5 h-5 text-amber-600" />
          <span>Cats</span>
        </Label>
        
        <Label 
          htmlFor="pet-dogs" 
          className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all ${formData.pet_type === 'dogs' ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:border-stone-300'}`}
        >
          <RadioGroupItem value="dogs" id="pet-dogs" />
          <Dog className="w-5 h-5 text-amber-600" />
          <span>Dogs</span>
        </Label>
        
        <Label 
          htmlFor="pet-other" 
          className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all ${formData.pet_type === 'other' ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:border-stone-300'}`}
        >
          <RadioGroupItem value="other" id="pet-other" />
          <Bird className="w-5 h-5 text-amber-600" />
          <span>Other</span>
        </Label>
        
        <Label 
          htmlFor="pet-none" 
          className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all ${formData.pet_type === 'none' ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:border-stone-300'}`}
        >
          <RadioGroupItem value="none" id="pet-none" />
          <Home className="w-5 h-5 text-amber-600" />
          <span>No pets</span>
        </Label>
      </RadioGroup>

      {formData.pet_type === 'none' && (
        <div className="flex items-start gap-3 p-4 bg-stone-50 rounded-lg">
          <Checkbox
            id="house-sitting"
            checked={formData.interested_in_house_sitting}
            onCheckedChange={(checked) => handleInputChange("interested_in_house_sitting", checked)}
            data-testid="profile-house-sitting-checkbox"
          />
          <div className="grid gap-1.5 leading-none">
            <Label htmlFor="house-sitting" className="cursor-pointer font-medium">
              Interested in cat sitting services?
            </Label>
            <p className="text-sm text-stone-500">
              We'll notify you about house-sitting opportunities
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6" data-testid="profile-step-3">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Cat className="w-8 h-8 text-amber-600" />
        </div>
        <h3 className="text-lg font-semibold text-stone-900">Tell us more about your pets</h3>
        <p className="text-sm text-stone-500">Help us understand your furry family</p>
      </div>

      <div className="space-y-4">
        {(formData.pet_type === 'cats' || formData.pet_type === 'other') && (
          <div>
            <Label htmlFor="num_cats">Number of cats</Label>
            <Input
              id="num_cats"
              type="number"
              min="0"
              max="50"
              value={formData.num_cats}
              onChange={(e) => handleInputChange("num_cats", parseInt(e.target.value) || 0)}
              data-testid="profile-num-cats-input"
            />
          </div>
        )}

        {(formData.pet_type === 'dogs' || formData.pet_type === 'other') && (
          <div>
            <Label htmlFor="num_dogs">Number of dogs</Label>
            <Input
              id="num_dogs"
              type="number"
              min="0"
              max="50"
              value={formData.num_dogs}
              onChange={(e) => handleInputChange("num_dogs", parseInt(e.target.value) || 0)}
              data-testid="profile-num-dogs-input"
            />
          </div>
        )}

        {formData.pet_type === 'other' && (
          <>
            <div>
              <Label htmlFor="num_other">Number of other pets</Label>
              <Input
                id="num_other"
                type="number"
                min="0"
                max="50"
                value={formData.num_other_pets}
                onChange={(e) => handleInputChange("num_other_pets", parseInt(e.target.value) || 0)}
                data-testid="profile-num-other-input"
              />
            </div>
            <div>
              <Label htmlFor="other_desc">What types of pets?</Label>
              <Input
                id="other_desc"
                placeholder="e.g., rabbits, hamsters, fish..."
                value={formData.other_pets_description}
                onChange={(e) => handleInputChange("other_pets_description", e.target.value)}
                data-testid="profile-other-desc-input"
              />
            </div>
          </>
        )}

        {formData.pet_type === 'none' && (
          <div className="text-center py-6 text-stone-500">
            <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p>Great! You're all set.</p>
            <p className="text-sm">Click "Complete" to finish your profile.</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleSkip()}>
      <DialogContent className="sm:max-w-md" data-testid="profile-completion-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PawPrint className="w-5 h-5 text-amber-600" />
            Complete Your Profile
          </DialogTitle>
          <DialogDescription>
            Step {step} of {totalSteps} - Help us personalize your experience
          </DialogDescription>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="flex gap-2 mb-4">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                s <= step ? 'bg-amber-500' : 'bg-stone-200'
              }`}
            />
          ))}
        </div>

        {/* Step content */}
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}

        {/* Navigation buttons */}
        <div className="flex justify-between mt-6 pt-4 border-t">
          <div>
            {step > 1 ? (
              <Button variant="ghost" onClick={prevStep} data-testid="profile-prev-btn">
                Back
              </Button>
            ) : (
              <Button variant="ghost" onClick={handleSkip} data-testid="profile-skip-btn">
                Skip for now
              </Button>
            )}
          </div>
          <Button 
            onClick={nextStep} 
            disabled={loading}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="profile-next-btn"
          >
            {loading ? "Saving..." : step === totalSteps ? "Complete" : "Next"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileCompletion;
